/**
 * Confidence score helper utilities
 */

import { ConfidenceLevel, ConfidenceInfo } from '@/types/models';

/**
 * Get confidence level from score
 */
export const getConfidenceLevel = (score: number): ConfidenceLevel => {
  if (score >= 0.85) return 'very_high';
  if (score >= 0.70) return 'high';
  if (score >= 0.50) return 'medium';
  return 'low';
};

/**
 * Get confidence info (level, color, label) from score
 */
export const getConfidenceInfo = (score: number): ConfidenceInfo => {
  const level = getConfidenceLevel(score);

  const infoMap: Record<ConfidenceLevel, ConfidenceInfo> = {
    very_high: {
      level: 'very_high',
      color: '#4caf50', // Green
      label: 'Very High',
      icon: '🟢',
    },
    high: {
      level: 'high',
      color: '#fbc02d', // Yellow
      label: 'High',
      icon: '🟡',
    },
    medium: {
      level: 'medium',
      color: '#ff9800', // Orange
      label: 'Medium',
      icon: '🟠',
    },
    low: {
      level: 'low',
      color: '#f44336', // Red
      label: 'Low',
      icon: '🔴',
    },
  };

  return infoMap[level];
};

/**
 * Format confidence score as percentage
 */
export const formatConfidence = (score: number): string => {
  return `${(score * 100).toFixed(0)}%`;
};

/**
 * Get confidence description
 */
export const getConfidenceDescription = (score: number): string => {
  const level = getConfidenceLevel(score);

  const descriptions: Record<ConfidenceLevel, string> = {
    very_high: 'Very likely correct - minimal review needed',
    high: 'Likely correct - review recommended',
    medium: 'Moderate confidence - careful review required',
    low: 'Low confidence - likely incorrect or needs significant editing',
  };

  return descriptions[level];
};
